import { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Stack,
  Title,
  Text,
  Badge,
  Button,
  Group,
  Grid,
  Flex,
  Spoiler,
  Card,
  Divider,
  ThemeIcon,
  Skeleton,
  AspectRatio,
  Box,
  Alert,
  Progress,
  RingProgress,
} from '@mantine/core';
import {
  IconPlayerPlay,
  IconLock,
  IconClock,
  IconListCheck,
  IconListDetails,
  IconCheck,
  IconAlertTriangle,
  IconCircleCheck,
  IconHourglassHigh,
  IconBookmarks,
  IconUserCheck,
} from '@tabler/icons-react';
import {
  usePublicCoursesService,
  useCourseService,
  useCourseSessionService,
  useCoursePrerequisiteService,
  useCourseSessionStatusService,
  useUserCourseProgressService,
} from '@actschurch/ccms-sdk';
import type { Course, CoursePrerequisite, CourseSession, CourseModelView, CourseProgressComputedModelView } from '@actschurch/ccms-sdk';
import { IconArrowRight } from '@tabler/icons-react';
import { useOptionalAuth } from '@/hooks/useOptionalAuth';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { SignInPromptModal } from '@/components/SignInPromptModal';
import { RichText, RichTextPreview } from '@/components/common/RichText';
import { courseHref, matchesSlug } from '@/utils/slug';
import { useCourseProgress } from '@/hooks/useCourseProgress';

const PUBLIC_VISIBILITY_NAME = 'Public';

/**
 * Course detail page — curriculum drawer + prerequisite gate + Start CTA.
 *
 * Route is <c>/courses/:slug</c>. The slug only lives in the URL — we resolve
 * it locally against the cached catalog so every SDK call still uses the
 * numeric <c>courseID</c>. Authed users get a second pass against the regular
 * Course endpoint so gated courses still resolve.
 */
export default function CourseDetail() {
  const params = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useOptionalAuth();
  const { user } = useEnrichedAuth();
  const userId = (user as { userId?: string; id?: string } | null)?.userId
    ?? (user as { id?: string } | null)?.id
    ?? null;

  const publicService = usePublicCoursesService();
  const courseService = useCourseService();
  const sessionService = useCourseSessionService();
  const prereqService = useCoursePrerequisiteService();
  const sessionStatusService = useCourseSessionStatusService();
  const userCourseProgressService = useUserCourseProgressService();

  const slug = params.slug;

  // URL is slug-only; the SDK still talks IDs. Pull the enriched catalog once
  // (every row carries CategoryName / MinistryName / VisibilityName so we
  // don't need follow-up lookup joins), resolve the slug locally, then key
  // every follow-up call off `course.courseID`.
  const { data: catalog = [], isLoading: loadingCatalog } = useQuery<CourseModelView[]>({
    queryKey: ['public', 'courses', 'view'],
    queryFn: () => publicService.listView(),
  });

  const { data: privateCatalog = [], isLoading: loadingPrivate } = useQuery<Course[]>({
    queryKey: ['courses', 'all', 'authed'],
    queryFn: () => courseService.getAll(),
    enabled: isAuthenticated,
  });

  // Both shapes carry the fields we need (slug/name/courseID/categoryID/etc.)
  // — keep them as a discriminated union so badges read fields safely.
  type ResolvedCourse = (CourseModelView & { __enriched: true }) | (Course & { __enriched?: false });

  const course = useMemo<ResolvedCourse | null>(() => {
    if (!slug) return null;
    const fromPublic = catalog.find((c) => matchesSlug(slug, c));
    if (fromPublic) return { ...fromPublic, __enriched: true } as ResolvedCourse;
    if (isAuthenticated) {
      const found = privateCatalog.find((c) => matchesSlug(slug, c));
      return found ? ({ ...found, __enriched: false } as ResolvedCourse) : null;
    }
    return null;
  }, [slug, catalog, privateCatalog, isAuthenticated]);

  const loadingCourse = loadingCatalog || (isAuthenticated && loadingPrivate);

  // Anonymous visitors call the public ID-based endpoints (visibility-gated
  // server-side); signed-in users still hit the admin endpoints to see gated
  // content end-to-end.
  const { data: rawSessions = [], isLoading: loadingSessions } = useQuery<CourseSession[]>({
    queryKey: ['course-sessions', course?.courseID, isAuthenticated],
    queryFn: () => {
      if (!course) return Promise.resolve([]);
      return isAuthenticated
        ? sessionService.getByCourse(course.courseID)
        : publicService.getSessionsByCourseId(course.courseID);
    },
    enabled: !!course?.courseID,
  });

  // Defensive draft filter — strips out draft sessions on the client too, so
  // even if the proxy is pointed at a backend that hasn't shipped the public
  // filter yet, draft sessions never appear in the curriculum drawer.
  //
  // Gated on isAuthenticated for two reasons: (1) the public endpoint already
  // filters drafts server-side, so the defensive pass adds no value for anon
  // viewers; (2) /course-session-statuses requires auth, so calling it from
  // an anon page just 401s on a loop. The 401 retries cause react-query to
  // hand a fresh empty-array reference on every re-render, which cascades
  // through draftSessionStatusIds → sessions → sessionIds → useCourseProgress
  // and triggers the "Maximum update depth exceeded" warning.
  //
  // Also use `retry: false` so a one-off permission failure doesn't keep
  // hitting the endpoint, and `staleTime` so the lookup is only ever fetched
  // once per session for the authed case.
  const { data: sessionStatuses = [] } = useQuery({
    queryKey: ['resources', 'course-session-statuses'],
    queryFn: () => sessionStatusService.getAll(),
    enabled: isAuthenticated,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const draftSessionStatusIds = useMemo(
    () => new Set(
      sessionStatuses
        .filter((s) => (s.name ?? '').trim().toLowerCase() === 'draft')
        .map((s) => s.statusID),
    ),
    [sessionStatuses],
  );
  // Sort by orderNo so the curriculum displays in the author's intended
  // sequence regardless of the server-side query order. Falls back to
  // sessionID for stable ordering when two sessions share an orderNo.
  const sessions = useMemo(
    () =>
      rawSessions
        .filter((s) => !draftSessionStatusIds.has(s.statusID))
        .slice()
        .sort((a, b) => {
          const oa = a.orderNo ?? Number.MAX_SAFE_INTEGER;
          const ob = b.orderNo ?? Number.MAX_SAFE_INTEGER;
          if (oa !== ob) return oa - ob;
          return (a.sessionID ?? 0) - (b.sessionID ?? 0);
        }),
    [rawSessions, draftSessionStatusIds],
  );

  // Per-session + aggregate progress. Signed-in viewers get the authoritative
  // server-computed progress (completion derived from UserSessionProgress vs
  // the course's current session set); anonymous viewers fall back to the
  // localStorage store. Both are normalised below so the UI is source-agnostic.
  const sessionIds = useMemo(() => sessions.map((s) => s.sessionID), [sessions]);
  const localProgress = useCourseProgress(sessionIds, userId);

  const { data: computedProgress } = useQuery<CourseProgressComputedModelView | null>({
    queryKey: ['course-progress-computed', course?.courseID],
    queryFn: () => (course ? userCourseProgressService.getMyComputedForCourse(course.courseID) : Promise.resolve(null)),
    enabled: !!course?.courseID && isAuthenticated,
    staleTime: 15_000,
  });

  // Unified progress: server-computed for signed-in viewers, localStorage for
  // guests. `summary` feeds the progress card; `perSession` feeds the
  // curriculum ticks.
  const courseProgress = useMemo(() => {
    const perSession = new Map<number, NormalizedSessionProgress>();
    if (isAuthenticated && computedProgress) {
      for (const e of computedProgress.perSession) {
        perSession.set(e.sessionID, {
          status: e.isCompleted ? 'completed' : (e.percentWatched ?? 0) > 0 ? 'in-progress' : 'not-started',
          pct: Math.round(Number(e.percentWatched ?? 0)),
        });
      }
      const completed = computedProgress.completedSessionCount;
      const total = computedProgress.totalSessionCount;
      const inProgress = [...perSession.values()].filter((p) => p.status === 'in-progress').length;
      return {
        summary: {
          total,
          completed,
          inProgress,
          notStarted: Math.max(0, total - completed - inProgress),
          percentComplete: Math.round(Number(computedProgress.percentComplete)),
        } as CourseProgressSummary,
        perSession,
      };
    }
    for (const id of sessionIds) {
      const e = localProgress.perSession[id];
      perSession.set(
        id,
        e
          ? { status: e.status, pct: e.duration ? Math.min(100, Math.round((e.position / e.duration) * 100)) : 0 }
          : { status: 'not-started', pct: 0 },
      );
    }
    return {
      summary: {
        total: localProgress.total,
        completed: localProgress.completed,
        inProgress: localProgress.inProgress,
        notStarted: localProgress.notStarted,
        percentComplete: localProgress.percentComplete,
      } as CourseProgressSummary,
      perSession,
    };
  }, [isAuthenticated, computedProgress, localProgress, sessionIds]);

  const { data: prereqs = [] } = useQuery<CoursePrerequisite[]>({
    queryKey: ['course-prerequisites', course?.courseID, isAuthenticated],
    queryFn: () => {
      if (!course) return Promise.resolve([]);
      return isAuthenticated
        ? prereqService.getByCourse(course.courseID)
        : publicService.getPrerequisitesByCourseId(course.courseID);
    },
    enabled: !!course?.courseID,
  });

  const { data: unmetPrereqs = [] } = useQuery<CoursePrerequisite[]>({
    queryKey: ['course-prerequisites-unmet', course?.courseID, isAuthenticated],
    queryFn: () => (course ? prereqService.getUnmetForMe(course.courseID) : Promise.resolve([])),
    enabled: !!course?.courseID && isAuthenticated,
  });

  // Signed-in viewer's computed progress across every course they've touched.
  // Used to render a per-prerequisite progress bar + "Resume / Start" CTA on
  // each prerequisite card. Anonymous viewers don't have progress, so the
  // query is gated on auth.
  const { data: myProgress = [] } = useQuery<CourseProgressComputedModelView[]>({
    queryKey: ['my-course-progress-computed'],
    queryFn: () => userCourseProgressService.getMyComputed(),
    enabled: isAuthenticated,
    staleTime: 30_000,
  });
  const myProgressByCourse = useMemo(
    () => new Map(myProgress.map((p) => [p.courseID, p])),
    [myProgress],
  );

  // Resolve a prerequisite's prerequisiteCourseID against the catalog we
  // already loaded so the card can show the real course name + slug for the
  // "Go to course" link, not just "#6". Falls back to the private catalog
  // when the public catalog doesn't include the course (e.g. gated content
  // that admins set as a prereq).
  const courseById = useMemo(() => {
    const m = new Map<number, CourseModelView | Course>();
    for (const c of catalog) m.set(c.courseID, c);
    for (const c of privateCatalog) if (!m.has(c.courseID)) m.set(c.courseID, c);
    return m;
  }, [catalog, privateCatalog]);

  const unmetPrereqIds = useMemo(
    () => new Set(unmetPrereqs.map((p) => p.prerequisiteCourseID)),
    [unmetPrereqs],
  );

  // All enrichment data is on the resolved course (when it came from the
  // enriched catalog). For the authed-fallback path, those fields are absent
  // — `categoryName`/`ministryName` are nullish and the badges hide.
  const enrichedCourse = course?.__enriched ? (course as CourseModelView) : null;
  const visibilityName = enrichedCourse?.visibilityName ?? null;
  const visibilityColor = enrichedCourse?.visibilityColor ?? null;
  const categoryName = enrichedCourse?.categoryName ?? null;
  const categoryColor = enrichedCourse?.categoryColor ?? null;
  const ministryName = enrichedCourse?.ministryName ?? null;

  const isPublic = visibilityName === PUBLIC_VISIBILITY_NAME;
  const isGated = course != null && !isPublic && !isAuthenticated;

  const requiredPrereqs = prereqs.filter((p) => p.isRequired);
  const recommendedPrereqs = prereqs.filter((p) => !p.isRequired);
  const canStart = !isGated && unmetPrereqs.length === 0;

  const [signInOpen, setSignInOpen] = useState(false);

  if (loadingCourse) {
    return (
      <Stack gap="lg">
        <Skeleton height={280} radius="lg" />
        <Skeleton height={24} w={320} />
        <Skeleton height={18} />
        <Skeleton height={18} w="60%" />
      </Stack>
    );
  }

  if (!course) {
    return (
      <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light">
        We couldn't find that course. It may have been moved or is not publicly available.
      </Alert>
    );
  }

  const handleStart = () => {
    if (isGated) {
      setSignInOpen(true);
      return;
    }
    // Belt-and-braces: button is already disabled when !canStart, but block
    // here too so a stale render or keyboard shortcut can't bypass the gate.
    if (!canStart) return;
    const first = sessions[0];
    if (!first) return;
    const slugBase = courseHref(course);
    navigate(`${slugBase}/play/${first.sessionID}`);
  };

  const scrollToCurriculum = () => {
    document.getElementById('curriculum')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <Stack gap="xl">
        <Card withBorder padding={0} radius="lg" shadow="sm" style={{ overflow: 'hidden' }}>
          {/* align="center" (not the default stretch) keeps the thumbnail at its
              natural 16:9 height instead of stretching it to match the content
              column — which is what cropped it into a tall sliver before. */}
          <Grid gutter={0} align="center">
            <Grid.Col span={{ base: 12, md: 5 }}>
              <AspectRatio ratio={16 / 9}>
                {course.thumbnail ? (
                  <img src={course.thumbnail} alt={course.name ?? 'Course'} style={{ objectFit: 'cover', width: '100%', height: '100%' }} />
                ) : (
                  <Box
                    style={{
                      width: '100%',
                      height: '100%',
                      background: course.color
                        ? `linear-gradient(135deg, ${course.color}aa, var(--mantine-color-brand-6))`
                        : 'linear-gradient(135deg, var(--mantine-color-brand-5), var(--mantine-color-brand-7))',
                    }}
                  />
                )}
              </AspectRatio>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 7 }}>
              <Stack p={{ base: 'lg', md: 'xl' }} gap="md">
                <Group gap="xs">
                  {ministryName && (
                    <Badge variant="light" color="brand">
                      {ministryName}
                    </Badge>
                  )}
                  {categoryName && (
                    <Badge variant="light" color={categoryColor ?? 'gray'}>
                      {categoryName}
                    </Badge>
                  )}
                  {visibilityName && !isPublic && (
                    <Badge variant="light" color={visibilityColor ?? 'gray'} leftSection={<IconLock size={10} />}>
                      {visibilityName}
                    </Badge>
                  )}
                  {sessions.length > 0 && (
                    <Badge variant="outline" leftSection={<IconClock size={10} />}>
                      {sessions.length} {sessions.length === 1 ? 'session' : 'sessions'}
                    </Badge>
                  )}
                </Group>

                <Title order={1}>{course.name ?? 'Untitled course'}</Title>

                {/* Primary actions live in the hero so Start + the curriculum are
                    one tap away without scrolling past the description. Buttons
                    stack full-width on mobile, sit inline from sm up. */}
                <Flex direction={{ base: 'column', sm: 'row' }} gap="sm" wrap="wrap" mt="xs">
                  <Button
                    size="md"
                    radius="md"
                    leftSection={canStart ? <IconPlayerPlay size={18} /> : <IconLock size={18} />}
                    // Block start when required prereqs are unmet OR the course
                    // has no sessions. Gated visitors still get a button so we
                    // can route them to the sign-in flow.
                    disabled={!isGated && (!canStart || sessions.length === 0)}
                    onClick={handleStart}
                  >
                    {isGated
                      ? 'Sign in to start'
                      : !canStart
                        ? 'Complete prerequisites first'
                        : 'Start course'}
                  </Button>
                  {sessions.length > 0 && (
                    <Button
                      size="md"
                      radius="md"
                      variant="light"
                      leftSection={<IconListDetails size={18} />}
                      onClick={scrollToCurriculum}
                    >
                      View curriculum
                    </Button>
                  )}
                  {isAuthenticated && (
                    <Button component={Link} to="/account" size="md" radius="md" variant="subtle">
                      My progress
                    </Button>
                  )}
                </Flex>
              </Stack>
            </Grid.Col>
          </Grid>
        </Card>

        {course.description && (
          <Card withBorder padding="lg" radius="lg">
            <Title order={4} mb="sm">About this course</Title>
            {/* Collapsed by default so the curriculum stays within easy reach;
                "Show more" reveals the full description in place. Spoiler hides
                its control automatically when the text already fits. */}
            <Spoiler maxHeight={150} showLabel="Show more" hideLabel="Show less">
              <RichText html={course.description} />
            </Spoiler>
          </Card>
        )}

        {requiredPrereqs.length > 0 && (
          <Card withBorder padding="lg" radius="lg">
            <Group gap="xs" mb="xs" wrap="nowrap">
              <IconListCheck size={18} color="var(--mantine-color-brand-6)" />
              <Title order={4} style={{ flex: 1 }}>Required prerequisites</Title>
              {!isAuthenticated ? (
                <Badge color="gray" variant="light">Sign in to track</Badge>
              ) : unmetPrereqs.length === 0 ? (
                <Badge color="teal" variant="light">All complete</Badge>
              ) : (
                <Badge color="red" variant="light">
                  {unmetPrereqs.length} of {requiredPrereqs.length} outstanding
                </Badge>
              )}
            </Group>
            <Text size="sm" c="dimmed" mb="md">
              You'll need to complete these before the Start button unlocks.
            </Text>
            <Stack gap="sm">
              {requiredPrereqs.map((p) => (
                <PrerequisiteRow
                  key={p.coursePrerequisiteID}
                  prereq={p}
                  prereqCourse={courseById.get(p.prerequisiteCourseID) ?? null}
                  progress={myProgressByCourse.get(p.prerequisiteCourseID) ?? null}
                  required
                  unmet={!isAuthenticated || unmetPrereqIds.has(p.prerequisiteCourseID)}
                  isAuthenticated={isAuthenticated}
                />
              ))}
            </Stack>
          </Card>
        )}

        {recommendedPrereqs.length > 0 && (
          <Card withBorder padding="lg" radius="lg">
            <Group gap="xs" mb="xs">
              <IconListCheck size={18} />
              <Title order={4}>Recommended first</Title>
            </Group>
            <Text size="sm" c="dimmed" mb="md">
              Not required, but they'll give you the best foundation.
            </Text>
            <Stack gap="sm">
              {recommendedPrereqs.map((p) => (
                <PrerequisiteRow
                  key={p.coursePrerequisiteID}
                  prereq={p}
                  prereqCourse={courseById.get(p.prerequisiteCourseID) ?? null}
                  progress={myProgressByCourse.get(p.prerequisiteCourseID) ?? null}
                  required={false}
                  unmet={false}
                  isAuthenticated={isAuthenticated}
                />
              ))}
            </Stack>
          </Card>
        )}

        {sessions.length > 0 && (
          <CourseProgressCard
            progress={courseProgress.summary}
            isAuthenticated={isAuthenticated}
            onSignIn={() => navigate(`/auth/login?redirect=${encodeURIComponent(window.location.pathname)}`)}
          />
        )}

        <Divider id="curriculum" label="Curriculum" labelPosition="center" style={{ scrollMarginTop: 80 }} />

        <Stack gap="xs">
          {loadingSessions ? (
            Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} height={60} radius="md" />)
          ) : sessions.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl">
              No sessions in this course yet.
            </Text>
          ) : (
            sessions.map((s: CourseSession, i: number) => {
              const slugBase = courseHref(course);
              // Lock the curriculum row when either:
              //   - the course is gated and the visitor is anonymous (we
              //     preview the first session only), OR
              //   - the viewer hasn't met the required prerequisites yet.
              // Without the prereq check, clicking any curriculum card
              // bypassed the Start-button gate entirely.
              const gateForAnon = isGated && i > 0;
              const gateForPrereqs = !isGated && !canStart;
              const disabled = gateForAnon || gateForPrereqs;
              const sessionProgress = courseProgress.perSession.get(s.sessionID) ?? null;
              const card = (
                <SessionCurriculumCard
                  index={i}
                  session={s}
                  disabled={disabled}
                  progress={sessionProgress}
                />
              );
              return disabled ? (
                <div key={s.sessionID}>{card}</div>
              ) : (
                <Link
                  key={s.sessionID}
                  to={`${slugBase}/play/${s.sessionID}`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  {card}
                </Link>
              );
            })
          )}
        </Stack>
      </Stack>

      <SignInPromptModal
        opened={signInOpen}
        onClose={() => setSignInOpen(false)}
        title="This course is for signed-in viewers"
        description="Sign in to unlock the full curriculum. Guests can preview the first session without an account."
      />
    </>
  );
}

function formatDuration(sec: number | null | undefined): string {
  if (!sec || sec <= 0) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}h ${m}m`;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Progress UI helpers
// ─────────────────────────────────────────────────────────────────────────────

interface CourseProgressSummary {
  total: number;
  completed: number;
  inProgress: number;
  notStarted: number;
  percentComplete: number;
}

/** Source-agnostic per-session progress (server-computed or localStorage). */
type NormalizedSessionProgress = {
  status: 'not-started' | 'in-progress' | 'completed';
  pct: number;
};

function CourseProgressCard({
  progress,
  isAuthenticated,
  onSignIn,
}: {
  progress: CourseProgressSummary;
  isAuthenticated: boolean;
  onSignIn: () => void;
}) {
  const { completed, total, percentComplete, inProgress } = progress;
  const isComplete = completed === total && total > 0;
  const ringColor = isComplete ? 'teal' : completed > 0 ? 'brand' : 'gray';
  return (
    <Card withBorder padding="lg" radius="lg" shadow="sm">
      <Group align="center" justify="space-between" wrap="wrap" gap="md">
        <Group gap="md" wrap="nowrap">
          <RingProgress
            size={68}
            thickness={6}
            roundCaps
            sections={[{ value: percentComplete, color: ringColor }]}
            label={
              <Text ta="center" size="xs" fw={700} c={ringColor}>
                {percentComplete}%
              </Text>
            }
          />
          <Stack gap={2}>
            <Text fw={600} size="sm">
              {isComplete
                ? 'You completed this course 🎉'
                : completed > 0
                  ? 'Your progress'
                  : inProgress > 0
                    ? "You've started this course"
                    : 'Track your progress'}
            </Text>
            <Text size="xs" c="dimmed">
              {completed} of {total} session{total === 1 ? '' : 's'} complete
              {inProgress > 0 && ` · ${inProgress} in progress`}
            </Text>
          </Stack>
        </Group>
        {!isAuthenticated && (completed > 0 || inProgress > 0) && (
          <Button
            variant="light"
            size="sm"
            leftSection={<IconUserCheck size={14} />}
            onClick={onSignIn}
          >
            Save to my profile
          </Button>
        )}
        {!isAuthenticated && completed === 0 && inProgress === 0 && (
          <Text size="xs" c="dimmed" maw={300}>
            Watching as a guest — sign in to save progress to your profile so it follows you
            across devices.
          </Text>
        )}
      </Group>
    </Card>
  );
}

function SessionCurriculumCard({
  index,
  session,
  disabled,
  progress,
}: {
  index: number;
  session: CourseSession;
  disabled: boolean;
  progress: NormalizedSessionProgress | null;
}) {
  const status = progress?.status ?? 'not-started';
  const completed = status === 'completed';
  const inProgress = status === 'in-progress';
  const pct = inProgress ? (progress?.pct ?? 0) : 0;

  // Visual cue for the row icon: green tick on completed, hourglass + colour
  // on in-progress, otherwise the default play arrow.
  const iconColor = disabled ? 'gray' : completed ? 'teal' : inProgress ? 'orange' : 'brand';
  const icon = disabled ? (
    <IconLock size={14} />
  ) : completed ? (
    <IconCircleCheck size={14} />
  ) : inProgress ? (
    <IconHourglassHigh size={14} />
  ) : (
    <IconPlayerPlay size={14} />
  );

  // Status + duration badges are reused on both layouts. Extracted so the
  // mobile (stacked-below) and desktop (side-by-side) JSX stays in sync. The
  // fixed minWidths only apply at sm+ where they help columns line up across
  // rows; on mobile each badge shrinks to fit its label so the row never
  // overflows on a 320–375px screen.
  const badgeMinWidthStatus = { base: undefined, sm: 96 };
  const badgeMinWidthDuration = { base: undefined, sm: 72 };

  const statusBadge = completed ? (
    <Badge
      size="sm"
      color="teal"
      variant="light"
      leftSection={<IconCheck size={10} />}
      styles={{ root: { justifyContent: 'center' } }}
      miw={badgeMinWidthStatus}
    >
      Done
    </Badge>
  ) : inProgress ? (
    <Badge
      size="sm"
      color="orange"
      variant="light"
      styles={{ root: { justifyContent: 'center' } }}
      miw={badgeMinWidthStatus}
    >
      {pct}%
    </Badge>
  ) : disabled ? (
    <Badge
      size="sm"
      color="gray"
      variant="light"
      leftSection={<IconLock size={10} />}
      styles={{ root: { justifyContent: 'center' } }}
      miw={badgeMinWidthStatus}
    >
      Locked
    </Badge>
  ) : (
    <Badge
      size="sm"
      color="gray"
      variant="light"
      leftSection={<IconBookmarks size={10} />}
      styles={{ root: { justifyContent: 'center' } }}
      miw={badgeMinWidthStatus}
    >
      Not started
    </Badge>
  );

  const durationBadge = (
    <Badge
      size="sm"
      variant="light"
      leftSection={<IconClock size={10} />}
      styles={{ root: { justifyContent: 'center' } }}
      miw={badgeMinWidthDuration}
    >
      {formatDuration(session.durationInSeconds)}
    </Badge>
  );

  const titleBlock = (
    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
      <ThemeIcon variant="light" color={iconColor} size={32} radius="xl" style={{ flex: '0 0 auto' }}>
        {icon}
      </ThemeIcon>
      <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
        <Text fw={500} lineClamp={1}>
          {index + 1}. {session.name}
        </Text>
        <RichTextPreview html={session.description} size="xs" c="dimmed" lineClamp={1} />
        {inProgress && !completed && (
          <Progress value={pct} size="xs" radius="xl" color="orange" mt={2} />
        )}
      </Stack>
    </Group>
  );

  return (
    <Card
      padding="md"
      radius="md"
      withBorder
      style={{
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {/* Desktop (≥ sm): icon + title on the left, status + duration columns
          on the right with fixed minWidths so rows line up. */}
      <Group
        visibleFrom="sm"
        justify="space-between"
        wrap="nowrap"
        gap="sm"
        align="center"
      >
        {titleBlock}
        <Group gap="xs" wrap="nowrap" justify="flex-end" style={{ flex: '0 0 auto' }}>
          {statusBadge}
          {durationBadge}
        </Group>
      </Group>

      {/* Mobile (< sm): icon + title fills the row, badges drop to a second
          row underneath. No fixed minWidths so each badge sizes to content
          and we never run out of horizontal space on small screens. */}
      <Stack hiddenFrom="sm" gap="xs">
        {titleBlock}
        <Group gap="xs" wrap="wrap" justify="flex-end">
          {statusBadge}
          {durationBadge}
        </Group>
      </Stack>
    </Card>
  );
}

/**
 * One prerequisite row inside the Required / Recommended cards.
 *
 * Layout adapts to viewport width:
 *  - desktop (≥ sm): icon + name + notes on the left, progress + CTA on
 *    the right in a single horizontal row.
 *  - mobile (< sm): icon + name stack on top, progress + CTA below.
 *
 * Behaviour:
 *  - If the prereq course is in the cache, we link to its public detail
 *    page; otherwise we just show the bare reference (no clickable CTA so
 *    we don't 404 the user out).
 *  - Anonymous viewers see no progress data (the backend can't tell who
 *    they are); they get a "Sign in to track" hint via the parent card
 *    header instead.
 *  - "Required + unmet" gets a red ring; everything else gets a teal
 *    check or a neutral dot.
 */
function PrerequisiteRow({
  prereq,
  prereqCourse,
  progress,
  required,
  unmet,
  isAuthenticated,
}: {
  prereq: CoursePrerequisite;
  prereqCourse: CourseModelView | Course | null;
  progress: CourseProgressComputedModelView | null;
  required: boolean;
  unmet: boolean;
  isAuthenticated: boolean;
}) {
  // Anon viewers don't have a progress record to compare against, so they
  // always show as "not tracked" rather than "unmet". Only an authenticated
  // viewer with a missing/incomplete UserCourseProgress row is truly unmet.
  const completedSessions = progress?.completedSessionCount ?? null;
  const totalSessions = progress?.totalSessionCount ?? null;
  const pct = Math.min(100, Math.max(0, Number(progress?.percentComplete ?? 0)));
  const completed = isAuthenticated && progress?.isCompleted === true;
  const inProgress = isAuthenticated && !completed && pct > 0;
  const trulyUnmet = isAuthenticated && required && unmet && !completed;

  const ringColor = completed
    ? 'teal'
    : trulyUnmet
      ? 'red'
      : inProgress
        ? 'orange'
        : !isAuthenticated
          ? 'gray'
          : 'gray';
  const ringIcon = completed
    ? <IconCheck size={14} />
    : trulyUnmet
      ? <IconLock size={14} />
      : inProgress
        ? <IconHourglassHigh size={14} />
        : <IconBookmarks size={14} />;

  const courseName = prereqCourse?.name ?? `Course #${prereq.prerequisiteCourseID}`;
  const href = prereqCourse ? courseHref(prereqCourse) : null;

  const ctaLabel = !isAuthenticated
    ? 'View course'
    : completed
      ? 'Review course'
      : inProgress
        ? 'Resume'
        : 'Start course';

  const statusBadge = !isAuthenticated ? null : completed ? (
    <Badge color="teal" variant="light" size="sm" leftSection={<IconCheck size={10} />}>Completed</Badge>
  ) : inProgress ? (
    <Badge color="orange" variant="light" size="sm">{pct.toFixed(0)}%</Badge>
  ) : trulyUnmet ? (
    <Badge color="red" variant="light" size="sm" leftSection={<IconLock size={10} />}>Required</Badge>
  ) : (
    <Badge color="gray" variant="light" size="sm">Not started</Badge>
  );

  // Progress bar — only meaningful for authenticated viewers, but we render
  // it for ALL authed states (including 0% "not started" + 100% "completed")
  // so every required-prereq card has a visible progress affordance. The
  // session-count breakdown comes from the server-side UserCourseProgress
  // row; we fall back to just "% complete" when the totals aren't populated.
  const progressBlock = isAuthenticated ? (
    <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
      <Progress
        value={pct}
        size="sm"
        radius="xl"
        color={completed ? 'teal' : trulyUnmet ? 'red' : inProgress ? 'orange' : 'gray'}
      />
      <Group gap="xs" wrap="nowrap" justify="space-between">
        <Text size="xs" c="dimmed">
          {completed
            ? 'Course completed'
            : completedSessions != null && totalSessions != null
              ? `${completedSessions} of ${totalSessions} session${totalSessions === 1 ? '' : 's'} done`
              : pct > 0
                ? 'In progress'
                : 'Not started yet'}
        </Text>
        <Text size="xs" c="dimmed" fw={500}>
          {pct.toFixed(0)}%
        </Text>
      </Group>
    </Stack>
  ) : (
    <Text size="xs" c="dimmed">Sign in to see your progress.</Text>
  );

  const goButton = href ? (
    <Button
      component={Link}
      to={href}
      size="xs"
      variant={trulyUnmet ? 'filled' : 'light'}
      color={trulyUnmet ? 'red' : completed ? 'teal' : inProgress ? 'orange' : 'brand'}
      rightSection={<IconArrowRight size={14} />}
    >
      {ctaLabel}
    </Button>
  ) : (
    <Badge size="sm" color="gray" variant="light">Unavailable</Badge>
  );

  const titleBlock = (
    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
      <ThemeIcon color={ringColor} variant="light" radius="xl" size={28} style={{ flex: '0 0 auto' }}>
        {ringIcon}
      </ThemeIcon>
      <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
        <Group gap="xs" wrap="wrap">
          <Text fw={600} size="sm" lineClamp={1} style={{ minWidth: 0 }}>{courseName}</Text>
          {statusBadge}
        </Group>
        {prereq.notes && (
          <Text size="xs" c="dimmed" lineClamp={2}>{prereq.notes}</Text>
        )}
      </Stack>
    </Group>
  );

  return (
    <Card
      withBorder
      padding="sm"
      radius="md"
      style={{
        borderColor: trulyUnmet
          ? 'var(--mantine-color-red-light-color, var(--mantine-color-red-4))'
          : completed
            ? 'var(--mantine-color-teal-light-color, var(--mantine-color-teal-4))'
            : undefined,
      }}
    >
      {/* Desktop: title left, progress bar middle, CTA right */}
      <Group visibleFrom="sm" gap="md" wrap="nowrap" align="center">
        <Box style={{ flex: '1 1 240px', minWidth: 0 }}>{titleBlock}</Box>
        <Box style={{ flex: '1 1 200px', minWidth: 0 }}>{progressBlock}</Box>
        <Box style={{ flex: '0 0 auto' }}>{goButton}</Box>
      </Group>

      {/* Mobile: stacked — title, progress, CTA */}
      <Stack hiddenFrom="sm" gap="xs">
        {titleBlock}
        {progressBlock}
        <Group justify="flex-end">{goButton}</Group>
      </Stack>
    </Card>
  );
}
